import isUndefined from 'lodash/isUndefined';
import { observer } from 'mobx-react';

import style from './TaskPatientData.module.scss';

import { isDefined } from '@utils/isDefined';

import { PatientLabelFormat, PatientSelect } from '@components/ModelSelect/PatientSelect';
import { MedicalCaseMultiSelect } from '@components/ModelSelect/MedicalCaseSelect';
import { Button, ButtonModifiers, ButtonTheme } from '@components/Button';

import { TaskSection } from '../TaskSection';
import { TaskInputSubstitutor } from '../TaskInputSubstitutor';
import { TaskAttributes } from 'chaika-test-attributes';
import { translate } from '@chaika/i18n/helper';
import { TaskFieldView } from '../TaskFieldView';

import { useTaskPatientData } from './hooks/useTaskPatientData';

interface Props {
    isPatientOptional?: boolean;
    canEditMedicalCase?: boolean;
}

export const TaskPatientData = observer(({ isPatientOptional = false, canEditMedicalCase }: Props): React.ReactNode => {
    const {
        canManagePatient,
        isPatientSectionVisible,
        isPatientEditable,
        isMedicalCaseEditable,
        isPatientSelectDisabled,
        medicalCaseIds,
        patientData,
        phone,
        mergedEhrIds,
        entryId,
        medicalCaseValues,
        medicalCases,
        onPatientSelect,
        onChangeMedicalCaseIds,
        onShowPatientSection,
        onRemovePatient,
    } = useTaskPatientData({ isPatientOptional, canEditMedicalCase });

    const [patientId, patientName] = patientData;

    if (!isPatientSectionVisible) {
        return canManagePatient ? (
            <Button
                theme={ButtonTheme.Secondary}
                modifiers={[ButtonModifiers.Small]}
                additionaClassNames={[style.selectPatientButton]}
                label={translate('selectPatientAndEpisode')}
                dataTest={TaskAttributes.Add}
                onClick={onShowPatientSection}
            />
        ) : null;
    }

    return (
        <TaskSection
            title={translate('patient')}
            titleAction={
                canManagePatient && (
                    <Button
                        theme={ButtonTheme.Transparent}
                        modifiers={[ButtonModifiers.WithoutBackground, ButtonModifiers.WithoutPaddings]}
                        additionaClassNames={[style.removePatientButton]}
                        label={translate('delete')}
                        dataTest={TaskAttributes.Delete}
                        onClick={onRemovePatient}
                    />
                )
            }
        >
            {
                <TaskInputSubstitutor
                    canEdit={isPatientEditable}
                    label={translate('fioEhrDate')}
                    value={patientName}
                    dataTest={TaskAttributes.PatientInfo}
                    onClick={() => window.open(`/patient/${patientId}`, '_blank')}
                >
                    <PatientSelect
                        forcePlaceholder
                        fromLastViewedPatients
                        additionalOptions={[{ label: translate('notSelected'), value: undefined }]}
                        disabled={isPatientSelectDisabled}
                        labelFormat={[
                            PatientLabelFormat.FullName,
                            PatientLabelFormat.RecordId,
                            PatientLabelFormat.BirthDay,
                        ]}
                        placeholder={translate('chooseFromList')}
                        title={translate('fioEhrDate')}
                        name={'patient'}
                        patientId={patientId}
                        onChange={onPatientSelect}
                        dataTest={TaskAttributes.PatientInfoSelect}
                    />
                </TaskInputSubstitutor>
            }
            {isDefined(phone) && !isPatientEditable && (
                <TaskFieldView
                    label={translate('phone')}
                    value={phone}
                    onClick={() => window.open(`tel:+${phone}`)}
                />
            )}
            <MedicalCaseMultiSelect
                canEdit={isMedicalCaseEditable}
                disabled={isUndefined(patientId)}
                name={'medicalCase'}
                patientId={patientId}
                medicalCaseValues={medicalCaseValues}
                mergedPatientIds={mergedEhrIds}
                entryId={entryId}
                medicalCases={medicalCases}
                medicalCaseIds={medicalCaseIds}
                onChangeMedicalCaseIds={onChangeMedicalCaseIds}
            />
        </TaskSection>
    );
});
